import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';

import {
  CURRENCY_TYPES,
  EMPTY_QUOTE_ENTRY,
  IPS_QUOTE_TEMPLATE_TYPES,
  REGION_TYPES
} from '../../data/RFXCommonStepsData';
import { RFXResponseForm, RFXResponseState } from '../../interfaces/RFXResponseForm';
import { RootState } from '../../store';

export interface RFXResponseFormState {
  currentRFXId: string;
  currentRound: number;
  currentRFXResponseForm: RFXResponseForm;
}

const initialState: RFXResponseFormState = {
  currentRFXId: '',
  currentRound: 1,
  currentRFXResponseForm: {
    state: RFXResponseState.DRAFT,
    round: 0,
    generalInformation: {
      requestId: '',
      rfxType: '',
      rfxName: '',
      currency: CURRENCY_TYPES[0],
      quoteTemplateType: IPS_QUOTE_TEMPLATE_TYPES[0],
      rfxLineItemClassId: '',
      region: REGION_TYPES[0],
      beginDate: '0',
      dueDate: '0',
      needByDate: '0',
      defaultQuoteValidity: undefined,
      overview: '',
      processDetails: ''
    },
    quotes: {
      quoteValidity: undefined,
      requestQuote: cloneDeep(EMPTY_QUOTE_ENTRY),
      quoteEntries: []
    },
    documents: {
      requestDocuments: [],
      documentEntries: []
    },
    contacts: {
      contacts: []
    },
    review: {}
  }
};

const rfxResponseFormSlice = createSlice({
  name: 'manageRFXResponse',
  initialState,
  reducers: {
    setCurrentRFXId(state, action) {
      state.currentRFXId = action.payload;
    },
    setCurrentRound(state, action) {
      state.currentRound = action.payload;
    },
    setCurrentRFXResponseForm(state, action) {
      state.currentRFXResponseForm = action.payload;
    },
    setQuoteValidity(state, action: PayloadAction<number | undefined>) {
      const newQuoteValidity = action.payload;
      if (state.currentRFXResponseForm.quotes.quoteValidity !== newQuoteValidity) {
        state.currentRFXResponseForm.quotes.quoteValidity = newQuoteValidity;
      }
    }
  }
});

export const {
  setCurrentRFXId,
  setCurrentRFXResponseForm,
  setCurrentRound,
  setQuoteValidity
} = rfxResponseFormSlice.actions;

export const selectCurrentRFXId = (state: RootState) =>
  state.manageRFXResponse.currentRFXId;

export const selectCurrentRound = (state: RootState) =>
  state.manageRFXResponse.currentRound;

export const selectCurrentRFXResponseForm = (state: RootState) =>
  state.manageRFXResponse.currentRFXResponseForm;

export const selectQuoteValidities = (state: RootState) => {
  const defaultQuoteValidity = state.manageRFXResponse.currentRFXResponseForm.generalInformation.defaultQuoteValidity;
  const quoteValidity = state.manageRFXResponse.currentRFXResponseForm.quotes.quoteValidity || defaultQuoteValidity;
  return {
    defaultQuoteValidity,
    quoteValidity
  };
};

export default rfxResponseFormSlice.reducer;
